import { useCallback, useEffect, useRef, useState } from 'react';

const SPEAKING_THRESHOLD = 18;

const playMicStartSound = () => {
    try {
        const context = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = context.createOscillator();
        const gain = context.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(740, context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(980, context.currentTime + 0.08);

        gain.gain.setValueAtTime(0.0001, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.18);

        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.18);

        oscillator.onended = () => {
            context.close().catch(() => {});
        };
    } catch (_) {
        // Optional feedback only.
    }
};

const playMicStopSound = () => {
    try {
        const context = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = context.createOscillator();
        const gain = context.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(680, context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(440, context.currentTime + 0.1);

        gain.gain.setValueAtTime(0.0001, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.1, context.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.16);

        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.16);

        oscillator.onended = () => {
            context.close().catch(() => {});
        };
    } catch (_) {
        // Optional feedback only.
    }
};

export default function useMicVisualizer() {
    const [isActive, setIsActive] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const [isSpeaking, setIsSpeaking] = useState(false);

    const streamRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const isActiveRef = useRef(false);
    const startingRef = useRef(false);

    const stopMic = useCallback(({ playSound = true } = {}) => {
        const wasActive = Boolean(streamRef.current);

        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }

        analyserRef.current = null;
        isActiveRef.current = false;
        startingRef.current = false;
        setIsActive(false);
        setAudioLevel(0);
        setIsSpeaking(false);

        if (playSound && wasActive) {
            playMicStopSound();
        }
    }, []);

    const startMic = useCallback(async () => {
        if (isActiveRef.current || streamRef.current || startingRef.current) {
            return;
        }

        startingRef.current = true;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();

            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.82;
            source.connect(analyser);

            streamRef.current = stream;
            audioContextRef.current = audioContext;
            analyserRef.current = analyser;

            playMicStartSound();
            isActiveRef.current = true;
            setIsActive(true);

            const buffer = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                if (!analyserRef.current) {
                    return;
                }

                analyserRef.current.getByteFrequencyData(buffer);
                const average = buffer.reduce((sum, value) => sum + value, 0) / buffer.length;
                const normalized = Math.min(average / 80, 1);

                setAudioLevel(normalized);
                setIsSpeaking(average > SPEAKING_THRESHOLD);
                rafRef.current = requestAnimationFrame(tick);
            };

            rafRef.current = requestAnimationFrame(tick);
        } finally {
            startingRef.current = false;
        }
    }, []);

    useEffect(() => {
        return () => {
            stopMic({ playSound: false });
        };
    }, [stopMic]);

    return {
        isActive,
        audioLevel,
        isSpeaking,
        startMic,
        stopMic,
    };
}
